/* tslint:disable:trailing-comma */
/* eslint-disable @typescript-eslint/camelcase */

import { PaletteTheme } from '@kite-js/theme-types';
import {
    alpha_black_15,
    alpha_black_30,
    alpha_black_80,
    alpha_white_20,
    alpha_white_30,
    background_100,
    background_150,
    background_20,
    background_200,
    background_300,
    background_50,
    background_500,
    background_800,
    blue_100,
    blue_300,
    blue_400,
    blue_500,
    flight_blue_300,
    flight_blue_50,
    flight_blue_500,
    green_100,
    green_300,
    green_400,
    green_50,
    green_500,
    indigo_100,
    indigo_300,
    indigo_400,
    nha_green_300,
    nha_green_50,
    nha_green_500,
    ocean_100,
    ocean_300,
    ocean_400,
    ocean_500,
    orange_100,
    orange_300,
    orange_400,
    orange_50,
    purple_100,
    purple_300,
    purple_400,
    purple_500,
    red_100,
    red_200,
    red_300,
    red_400,
    red_50,
    red_500,
    text_000,
    text_100,
    text_300,
    text_500,
    text_800,
    white,
    yellow_100,
    yellow_200,
    yellow_400,
    yellow_500,
} from './colors';
import { coreDefaultTheme } from '@kite-js/theme';

const palette: Partial<PaletteTheme> = {
    primary: {
        darkSolid: {
            background: blue_500,
            text: white,
        },
        solid: {
            background: blue_300,
            text: white,
        },
        light: {
            background: blue_100,
            text: blue_300,
        },
        bordered: {
            background: white,
            border: blue_300,
            text: blue_300,
        },
        plainText: {
            text: blue_300,
        },
    },
    secondary: {
        darkSolid: {
            background: background_800,
            text: white,
        },
        solid: {
            background: background_300,
            text: white,
        },
        light: {
            background: background_100,
            text: text_800,
        },
        bordered: {
            background: white,
            border: background_100,
            text: text_800,
        },
        plainText: {
            text: text_800,
        },
    },
    darkmode: {
        darkSolid: {
            background: background_800,
            text: white,
        },
        solid: {
            background: background_300,
            text: white,
        },
        light: {
            background: background_100,
            text: text_800,
        },
        bordered: {
            background: white,
            border: background_100,
            text: text_800,
        },
        plainText: {
            text: background_300,
        },
        gradient: {
            backgroundStart: background_300,
            backgroundEnd: background_800,
            text: white,
        },
    },
    price: {
        darkSolid: {
            background: green_500,
            text: white,
        },
        solid: {
            background: green_300,
            text: white,
        },
        light: {
            background: green_100,
            text: green_400,
        },
        bordered: {
            background: white,
            border: green_300,
            text: green_300,
        },
        plainText: {
            text: green_300,
        },
    },
    disable: {
        darkSolid: {
            background: background_150,
            text: white,
        },
        solid: {
            background: background_100,
            text: white,
        },
        light: {
            background: background_20,
            text: text_300,
        },
        bordered: {
            background: white,
            border: background_100,
            text: text_300,
        },
        plainText: {
            text: text_300,
        },
    },
    success: {
        darkSolid: {
            background: green_500,
            text: white,
        },
        solid: {
            background: green_300,
            text: white,
        },
        light: {
            background: green_50,
            text: green_400,
        },
        bordered: {
            background: white,
            border: green_100,
            text: green_300,
        },
        plainText: {
            text: green_300,
        },
        gradient: {
            backgroundStart: green_400,
            backgroundEnd: green_300,
            text: white,
        },
    },
    warning: {
        darkSolid: {
            background: orange_400,
            text: white,
        },
        solid: {
            background: orange_300,
            text: white,
        },
        light: {
            background: orange_50,
            text: green_300,
        },
        bordered: {
            background: white,
            border: orange_100,
            text: orange_300,
        },
        plainText: {
            text: orange_300,
        },
        gradient: {
            backgroundStart: orange_400,
            backgroundEnd: orange_300,
            text: white,
        },
    },
    error: {
        darkSolid: {
            background: red_500,
            text: white,
        },
        solid: {
            background: red_300,
            text: white,
        },
        light: {
            background: red_50,
            text: red_300,
        },
        bordered: {
            background: white,
            border: red_100,
            text: red_300,
        },
        plainText: {
            text: red_300,
        },
        gradient: {
            backgroundStart: red_300,
            backgroundEnd: red_200,
            text: white,
        },
    },
    notice: {
        darkSolid: {
            background: background_800,
            text: white,
        },
        solid: {
            background: background_500,
            text: white,
        },
        light: {
            background: blue_100,
            text: blue_400,
        },
        bordered: {
            background: white,
            border: background_150,
            text: text_800,
        },
        plainText: {
            text: blue_400,
        },
        gradient: {
            backgroundStart: blue_500,
            backgroundEnd: blue_300,
            text: white,
        },
    },
    positive: {
        darkSolid: {
            background: green_500,
            text: white,
        },
        solid: {
            background: green_300,
            text: white,
        },
        light: {
            background: green_50,
            text: green_400,
        },
        bordered: {
            background: white,
            border: green_100,
            text: green_300,
        },
        plainText: {
            text: green_300,
        },
    },
    urgency: {
        darkSolid: {
            background: orange_400,
            text: white,
        },
        solid: {
            background: orange_300,
            text: white,
        },
        light: {
            background: orange_50,
            text: orange_300,
        },
        bordered: {
            background: white,
            border: orange_100,
            text: orange_300,
        },
        plainText: {
            text: orange_300,
        },
    },
    alert: {
        darkSolid: {
            background: red_400,
            text: white,
        },
        solid: {
            background: red_300,
            text: white,
        },
        light: {
            background: red_50,
            text: red_300,
        },
        bordered: {
            background: white,
            border: red_100,
            text: red_300,
        },
        plainText: {
            text: red_300,
        },
    },
    neutral: {
        darkSolid: {
            background: background_800,
            text: white,
        },
        solid: {
            background: background_500,
            text: white,
        },
        light: {
            background: blue_100,
            text: blue_400,
        },
        bordered: {
            background: white,
            border: background_150,
            text: text_800,
        },
        plainText: {
            text: blue_400,
        },
    },
    promo: {
        darkSolid: {
            background: yellow_500,
            text: white,
        },
        solid: {
            background: yellow_400,
            text: white,
        },
        light: {
            background: yellow_100,
            text: yellow_500,
        },
        bordered: {
            background: white,
            border: yellow_400,
            text: yellow_500,
        },
        plainText: {
            text: yellow_500,
        },
    },
    cash: {
        darkSolid: {
            background: indigo_400,
            text: white,
        },
        solid: {
            background: indigo_300,
            text: white,
        },
        light: {
            background: indigo_100,
            text: indigo_400,
        },
        bordered: {
            background: white,
            border: indigo_100,
            text: indigo_300,
        },
        plainText: {
            text: indigo_300,
        },
    },
    vipSilver: {
        darkSolid: {
            background: background_800,
            text: yellow_200,
        },
        solid: {
            background: yellow_200,
            text: text_800,
        },
        light: {
            background: yellow_100,
            text: yellow_500,
        },
        bordered: {
            background: white,
            border: yellow_400,
            text: yellow_400,
        },
        plainText: {
            text: yellow_400,
        },
    },
    vipGold: {
        darkSolid: {
            background: background_800,
            text: yellow_200,
        },
        solid: {
            background: yellow_200,
            text: text_800,
        },
        light: {
            background: yellow_100,
            text: yellow_500,
        },
        bordered: {
            background: white,
            border: yellow_400,
            text: yellow_400,
        },
        plainText: {
            text: yellow_400,
        },
    },
    home: {
        darkSolid: {
            background: nha_green_500,
            text: white,
        },
        solid: {
            background: nha_green_300,
            text: white,
        },
        light: {
            background: nha_green_50,
            text: nha_green_500,
        },
        bordered: {
            background: white,
            border: nha_green_500,
            text: nha_green_500,
        },
        plainText: {
            text: nha_green_500,
        },
        gradient: {
            backgroundStart: nha_green_500,
            backgroundEnd: nha_green_300,
            text: white,
        },
    },
    family: {
        darkSolid: {
            background: purple_500,
            text: white,
        },
        solid: {
            background: purple_300,
            text: white,
        },
        light: {
            background: purple_100,
            text: purple_400,
        },
        bordered: {
            background: white,
            border: purple_300,
            text: purple_300,
        },
        plainText: {
            text: purple_300,
        },
    },
    new: {
        darkSolid: {
            background: ocean_500,
            text: white,
        },
        solid: {
            background: ocean_300,
            text: white,
        },
        light: {
            background: ocean_100,
            text: ocean_400,
        },
        bordered: {
            background: white,
            border: ocean_300,
            text: ocean_300,
        },
        plainText: {
            text: ocean_300,
        },
    },
    flights: {
        darkSolid: {
            background: flight_blue_500,
            text: white,
        },
        solid: {
            background: flight_blue_300,
            text: white,
        },
        light: {
            background: flight_blue_50,
            text: flight_blue_500,
        },
        bordered: {
            background: white,
            border: flight_blue_500,
            text: flight_blue_500,
        },
        plainText: {
            text: flight_blue_500,
        },
        gradient: {
            backgroundStart: flight_blue_500,
            backgroundEnd: flight_blue_300,
            text: white,
        },
    },
    text: {
        0: text_000,
        100: text_100,
        300: text_300,
        500: text_500,
        800: text_800,
        inherit: 'inherit',
    },
    background: {
        0: white,
        20: background_20,
        50: background_50,
        100: background_100,
        150: background_150,
        200: background_200,
        300: background_300,
        500: background_500,
        800: background_800,
        gradient: {
            start: background_50,
            end: background_150,
        },
    },
    dim: {
        pressState: alpha_black_15,
        modal: alpha_black_30,
        coachmark: alpha_black_80,
        navigationTab: alpha_white_20,
        hoverState: alpha_white_30,
    },
};

export default {
    ...coreDefaultTheme.palette,
    ...palette,
};
